"use client";

import * as React from "react";

import MultiCarousel from "@stadium/ui/src/components/multi-carousel";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";
import Button from "@stadium/shared/src/components/shared/button";

import { usePlasmicQueryData } from "@stadium/services/plasmic";

import { fetchPlasmicCMSData } from "@stadium/shared/src/helpers/plasmic/cms-api";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import {
  PLASMIC_TESTIMONIAL_QUERY_KEY,
  T002_TABLE_ID,
  CMS_SECTION_FIELDS,
  TESTIMONIAL_SLIDE_FIELDS,
  TestimonialSlide,
} from "./constants";

import styles from "./main.module.scss";

interface CustomDotProps {
  carouselState?: {
    currentSlide?: number;
  };
  onSlideChange?: (index: number) => void;
}

const HiddenCarouselDot: React.FC<CustomDotProps> = () => null; // dots hidden but carousel uses them for state

export interface T002Props {
  className?: string;
  testimonialData?: any;
}

const T02: React.FC<Readonly<T002Props>> = ({ className, testimonialData }) => {
  const CARD_WIDTH_XL = 1036;
  const carouselContainerRef = React.useRef<HTMLDivElement>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0);
  const [xlGutter, setXlGutter] = React.useState(400);

  React.useEffect(() => {
    const updateGutter = () => {
      const el = carouselContainerRef.current;
      if (el && window.innerWidth >= 1200) {
        setXlGutter(Math.max(0, el.clientWidth - CARD_WIDTH_XL));
      }
    };

    updateGutter();
    window.addEventListener("resize", updateGutter);
    return () => window.removeEventListener("resize", updateGutter);
  }, []);

  const testimonialCacheKey = testimonialData && JSON.stringify(testimonialData);

  const { data: cmsData } = usePlasmicQueryData(
    [PLASMIC_TESTIMONIAL_QUERY_KEY, testimonialCacheKey],
    async () => {
      if (testimonialData?.[TESTIMONIAL_SLIDE_FIELDS.HEADING]) {
        return { testimonial: testimonialData };
      }

      const data = await fetchPlasmicCMSData({ tableId: T002_TABLE_ID });

      if (!data) return null;

      return {
        testimonial: data[CMS_SECTION_FIELDS.TESTIMONIAL],
      };
    },
  );

  const testimonialSlides =
    (cmsData?.testimonial?.[TESTIMONIAL_SLIDE_FIELDS.TESTIMONIAL_SLIDE] as TestimonialSlide[]) ||
    [];
  const heading = cmsData?.testimonial?.[TESTIMONIAL_SLIDE_FIELDS.HEADING];

  const handlePrev = () => {
    if (testimonialSlides.length <= 0) return;
    const prevButton = carouselContainerRef.current?.querySelector(
      ".react-multiple-carousel__arrow--left",
    );
    (prevButton as HTMLButtonElement)?.click();
  };

  const handleNext = () => {
    if (testimonialSlides.length <= 0) return;
    const nextButton = carouselContainerRef.current?.querySelector(
      ".react-multiple-carousel__arrow--right",
    );
    (nextButton as HTMLButtonElement)?.click();
  };

  const handleAfterChange = (
    _: number,
    { currentSlide, slidesToShow }: { currentSlide: number; slidesToShow: number },
  ) => {
    const total = testimonialSlides.length;
    if (total === 0) return;
    // In infinite mode, carousel prepends `slidesToShow` clones — normalize to data index
    const normalized = (((currentSlide - slidesToShow) % total) + total) % total;
    setCurrentSlideIndex(normalized);
  };

  const { isDesktopDevice } = React.useContext(CurrentDeviceContext);

  return (
    <section className={`${styles.sectionWrapper} ${className || ""}`}>
      <div className={styles.contentWrapper}>
        <h1 className={styles.heading}>{heading}</h1>
        <div ref={carouselContainerRef} className={styles.carouselContainer}>
          <MultiCarousel
            responsive={{
              xlDesktop: {
                breakpoint: { max: 4000, min: 1200 },
                items: 1,
                slidesToSlide: 1,
                partialVisibilityGutter: xlGutter,
              },
              desktop: {
                breakpoint: { max: 1199, min: 1024 },
                items: 1,
                slidesToSlide: 1,
                partialVisibilityGutter: 0,
              },
              tablet: {
                breakpoint: { max: 1023, min: 768 },
                items: 1,
                slidesToSlide: 1,
                partialVisibilityGutter: 0,
              },
              mobile: {
                breakpoint: { max: 767, min: 0 },
                items: 1,
                slidesToSlide: 1,
                partialVisibilityGutter: 0,
              },
            }}
            partialVisible={true}
            showDots={true}
            arrows={true}
            draggable={false}
            swipeable={false}
            infinite={true}
            itemClass={styles.slideItem}
            customDot={<HiddenCarouselDot />}
            renderDotsOutside={true}
            afterChange={handleAfterChange}
          >
            {testimonialSlides.map((slide) => (
              <div
                key={`${slide[TESTIMONIAL_SLIDE_FIELDS.SLIDE_IMAGE]?.url}-${
                  slide[TESTIMONIAL_SLIDE_FIELDS.AUTHOR_NAME]
                }`}
                className={styles.slideCard}
              >
                <Multimedia
                  type="image"
                  src={slide[TESTIMONIAL_SLIDE_FIELDS.SLIDE_IMAGE]?.url || ""}
                  alt={slide[TESTIMONIAL_SLIDE_FIELDS.SLIDE_ALT_TEXT] || "Testimonial slide"}
                  className={styles.slideImage}
                />
              </div>
            ))}
          </MultiCarousel>
        </div>
        <div className={styles.reviewContainer}>
          {isDesktopDevice && (
            <div className={styles.navigationWrapper}>
              <Button styleType="no-style" className={styles.buttonWrapper} onClick={handlePrev}>
                <span className={styles.prevButton}></span>
              </Button>
              <Button styleType="no-style" className={styles.buttonWrapper} onClick={handleNext}>
                <span className={styles.nextButton}></span>
              </Button>
            </div>
          )}

          <div className={styles.reviewContentWrapper}>
            {testimonialSlides.map((slide, index) => (
              <div
                key={`${slide[TESTIMONIAL_SLIDE_FIELDS.SLIDE_IMAGE]?.url}-${
                  slide[TESTIMONIAL_SLIDE_FIELDS.AUTHOR_NAME]
                }`}
                className={`${styles.reviewWrapper} ${
                  index === currentSlideIndex ? styles.reviewActive : styles.reviewHidden
                }`}
              >
                <div className={styles.authorBrandWrapper}>
                  <div className={styles.authorInfo}>
                    <Multimedia
                      type="image"
                      src={slide[TESTIMONIAL_SLIDE_FIELDS.AUTHOR_AVATAR]?.url || ""}
                      alt={slide[TESTIMONIAL_SLIDE_FIELDS.AUTHOR_AVATAR_ALT] || "Author avatar"}
                      className={styles.authorImage}
                    />
                    <div>
                      <h2 className={styles.authorName}>
                        {slide[TESTIMONIAL_SLIDE_FIELDS.AUTHOR_NAME]}
                      </h2>
                      <p className={styles.authorDesignation}>
                        {slide[TESTIMONIAL_SLIDE_FIELDS.AUTHOR_DESIGNATION]}
                      </p>
                    </div>
                  </div>
                  <div className={styles.brandLogo}>
                    <Multimedia
                      type="image"
                      src={slide[TESTIMONIAL_SLIDE_FIELDS.BRAND_LOGO]?.url || ""}
                      alt={slide[TESTIMONIAL_SLIDE_FIELDS.AUTHOR_NAME] || "Brand logo"}
                      className={styles.brandLogoImage}
                    />
                  </div>
                </div>
                <p className={styles.reviewText}>
                  {slide[TESTIMONIAL_SLIDE_FIELDS.TESTIMONIAL_CONTENT]}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {!isDesktopDevice && (
        <div className={styles.navigationWrapper}>
          <Button styleType="no-style" className={styles.buttonWrapper} onClick={handlePrev}>
            <span className={styles.prevButton}></span>
          </Button>
          <Button styleType="no-style" className={styles.buttonWrapper} onClick={handleNext}>
            <span className={styles.nextButton}></span>
          </Button>
        </div>
      )}
    </section>
  );
};

export default T02;
